import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { escapeHtml } from '../utils/catalog-labels';
import { formatRoutineDate, routineStatusBadge } from '../utils/routine-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
}

interface RoutineSummary {
  id: string;
  name: string;
  description?: string | null;
  startDate: string;
  endDate?: string | null;
  isActive: boolean;
  effectiveStatus?: import('../utils/routine-labels').RoutineEffectiveStatus;
  exercises?: { id: string }[];
}

export class AdminRoutinesPage implements View {
  private students: Student[] = [];
  private routines: RoutineSummary[] = [];
  private selectedStudentId = '';
  private studentSearch = '';
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    const params = router.getParams();
    await this.loadData(params.studentId);

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-routines-loading"><p>Cargando rutinas...</p></div>`;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-routines-error">
          <h3>Error al cargar rutinas</h3>
          <p>${escapeHtml(this.errorMsg)}</p>
        </div>`;
    }

    const student = this.students.find((s) => s.id === this.selectedStudentId);

    const breadcrumbHtml = student
      ? `<nav class="admin-breadcrumb">
          <a href="#/admin/students">Alumnos</a>
          <span class="admin-breadcrumb-sep">›</span>
          <a href="#/admin/student?id=${student.id}">${student.firstName} ${student.lastName}</a>
          <span class="admin-breadcrumb-sep">›</span>
          <span>Rutinas</span>
        </nav>`
      : `<nav class="admin-breadcrumb">
          <a href="#/admin/students">Alumnos</a>
          <span class="admin-breadcrumb-sep">›</span>
          <span>Rutinas</span>
        </nav>`;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          ${breadcrumbHtml}
          <h2 data-testid="admin-routines-title">Rutinas asignadas</h2>
          <p>Gestiona planes activos e históricos por alumno.</p>
        </div>
        ${
          this.selectedStudentId
            ? `<a href="#/admin/routine?form=create&studentId=${this.selectedStudentId}" class="btn btn-primary" data-testid="btn-new-routine" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('plus', { size: 16 })} Nueva rutina</a>`
            : ''
        }
      </div>

      <div class="admin-filters card-glass" data-testid="admin-routines-filters">
        <div class="admin-filter-col admin-filter-col--grow">
          <label class="admin-filter-label" for="routine-student-search">Alumno</label>
          <div class="admin-combobox" data-testid="routine-student-select">
            <div class="admin-combobox-control">
              <span class="admin-combobox-icon">${getTyrisIcon('search', { size: 18 })}</span>
              <input
                id="routine-student-search"
                type="text"
                class="admin-input"
                placeholder="Buscar alumno por nombre o correo..."
                value="${escapeHtml(this.studentSearch)}"
                autocomplete="off"
              />
              ${student
                ? `<button type="button" class="admin-combobox-close" id="routine-student-clear" title="Quitar selección">${getTyrisIcon('close', { size: 16 })}</button>`
                : ''}
            </div>
            <div class="admin-combobox-menu" data-testid="admin-combobox-menu">
              ${this.renderComboboxMenu()}
            </div>
            ${student
              ? `<span class="admin-combobox-selected" data-testid="admin-routines-selected">${getTyrisIcon('perfil', { size: 15 })} ${escapeHtml(student.firstName)} ${escapeHtml(student.lastName)}</span>`
              : ''}
          </div>
        </div>
      </div>

      ${
        student
          ? `<p class="admin-cell-muted admin-routines-label" data-testid="admin-routines-student-label">
              Rutinas de <strong>${escapeHtml(student.firstName)} ${escapeHtml(student.lastName)}</strong>
            </p>`
          : ''
      }

      <div class="card-glass admin-table-wrap" data-testid="admin-routines-table">
        ${
          this.routines.length === 0
            ? `<div class="admin-empty" data-testid="admin-routines-empty"><p>${student ? 'Este alumno no tiene rutinas registradas.' : 'Seleccioná un alumno para ver sus rutinas asignadas.'}</p></div>`
            : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th>Ejercicios</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${this.routines.map((r) => this.renderRow(r)).join('')}
            </tbody>
          </table>
        `
        }
      </div>
    `;
  }

  afterRender(): void {
    const searchInput = document.getElementById('routine-student-search') as HTMLInputElement | null;
    const menu = document.querySelector<HTMLElement>('[data-testid="admin-combobox-menu"]');

    if (searchInput && menu) {
      const open = () => menu.classList.add('open');
      const close = () => menu.classList.remove('open');

      const refreshMenu = () => {
        menu.innerHTML = this.renderComboboxMenu();
        bindOptions();
        open();
      };

      const bindOptions = () => {
        menu.querySelectorAll('[data-student-pick]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const id = (btn as HTMLElement).getAttribute('data-student-pick');
            if (id) {
              window.location.hash = `#/admin/routines?studentId=${id}`;
            }
          });
        });
      };

      searchInput.addEventListener('focus', open);
      searchInput.addEventListener('input', () => {
        this.studentSearch = searchInput.value;
        refreshMenu();
      });
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          close();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const first = menu.querySelector<HTMLElement>('[data-student-pick]');
          if (first) first.click();
        }
      });
      document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.admin-combobox')) close();
      });

      bindOptions();

      document.getElementById('routine-student-clear')?.addEventListener('click', () => {
        window.location.hash = '#/admin/routines';
      });
    }
  }

  private renderComboboxMenu(): string {
    const selected = this.students.find((s) => s.id === this.selectedStudentId);
    return this.renderComboboxOptions(selected);
  }

  private renderComboboxOptions(selected: Student | undefined): string {
    const q = this.studentSearch.trim().toLowerCase();
    const pool = this.students.filter((s) => !s.role || s.role === 'STUDENT');
    const matches = q
      ? pool.filter((s) =>
          `${s.firstName} ${s.lastName} ${s.email}`.toLowerCase().includes(q),
        )
      : pool;

    if (matches.length === 0) {
      return `<div class="admin-combobox-empty">Sin coincidencias para "${escapeHtml(this.studentSearch)}".</div>`;
    }

    return matches
      .map((s) => {
        const isSel = selected && selected.id === s.id;
        return `
          <button type="button" class="admin-combobox-option ${isSel ? 'is-hover' : ''}" data-student-pick="${s.id}" data-testid="student-pick-${s.id}">
            ${getTyrisIcon('perfil', { size: 18 })}
            <span>
              <strong>${escapeHtml(s.firstName)} ${escapeHtml(s.lastName)}</strong>
              <small>${escapeHtml(s.email)}</small>
            </span>
          </button>`;
      })
      .join('');
  }

  private renderRow(r: RoutineSummary): string {
    const badge = routineStatusBadge(r);
    const period = `${formatRoutineDate(r.startDate)} – ${formatRoutineDate(r.endDate)}`;
    const count = r.exercises?.length ?? 0;

    return `
      <tr data-testid="routine-row-${r.id}">
        <td>
          <span class="admin-cell-primary">${escapeHtml(r.name)}</span>
          ${r.description ? `<div class="admin-cell-muted">${escapeHtml(r.description.slice(0, 60))}${r.description.length > 60 ? '…' : ''}</div>` : ''}
        </td>
        <td>${period}</td>
        <td><span class="${badge.className}">${badge.label}</span></td>
        <td>${count}</td>
        <td>
          <a href="#/admin/routine?id=${r.id}" class="btn btn-outline btn-sm" data-testid="routine-detail-link-${r.id}">Ver →</a>
        </td>
      </tr>
    `;
  }

  private async loadData(paramStudentId?: string): Promise<void> {
    try {
      this.students = await apiClient.get<Student[]>('/api/users/students');
      if (paramStudentId && this.students.some((s) => s.id === paramStudentId)) {
        this.selectedStudentId = paramStudentId;
      } else {
        this.selectedStudentId = '';
      }

      if (this.selectedStudentId) {
        this.routines = await apiClient.get<RoutineSummary[]>(
          `/api/routines/student/${this.selectedStudentId}`,
        );
      } else {
        this.routines = [];
      }
      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar rutinas';
    } finally {
      this.loading = false;
    }
  }
}
